import { MediumFormatting } from "@faire/web/common/localization/formatting";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";

import { useFaireDirectFirstOrderIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { getSettingEnableFaireDirectAutoExtendFirstOrderIncentive } from "@faire/retailer/settings/getSettingEnableFaireDirectAutoExtendFirstOrderIncentive";

import { OpenFaireDirectFirstOrderIncentiveModalButton } from "./__internal__/OpenFaireDirectFirstOrderIncentiveModalButton";

export const FaireDirectAutoExtendBanner: React.FC = () => {
  const {
    brandName,
    remainingDaysBeforeExpiry,
    isLastDayBeforeExpiry,
    isFirstDayInAutoExtendPeriod,
    isFaireDirectCreditPercentageBased,
    formattedFixedBasedAmount,
    formattedPercentageBasedNumber,
  } = useFaireDirectFirstOrderIncentive();

  if (!getSettingEnableFaireDirectAutoExtendFirstOrderIncentive()) {
    return null;
  }

  if (isFirstDayInAutoExtendPeriod) {
    if (isFaireDirectCreditPercentageBased) {
      return (
        <Typography align="center">
          <StrictLocalMsg
            defaultMessage="<MediumFormatting>Offer extended!</MediumFormatting> Get {faireDirectPercentageDiscountNumber} off & free shipping with {brandCompanyName}. <LinkFormatting>View details</LinkFormatting>"
            description={{
              text: "Banner at the top of the page that promotes a Faire Direct percentage based discount with a countdown time to remain retailer to spend their Faire Direct sign up credit before expires.",
              img: "https://cdn.faire.com/fastly/e540b9ee24ec6c131b9e7e90d8ac6b8f4540a84b4b5a27c3db814bbe5f0958c4.png",
            }}
            values={{
              MediumFormatting: MediumFormatting,
              LinkFormatting: OpenFaireDirectFirstOrderIncentiveModalButton,
              faireDirectPercentageDiscountNumber:
                formattedPercentageBasedNumber,
              brandCompanyName: brandName ?? "",
            }}
          />
        </Typography>
      );
    } else {
      return (
        <Typography align="center">
          <StrictLocalMsg
            defaultMessage="<MediumFormatting>Offer extended!</MediumFormatting> Get {FaireDirectSignUpDiscountMoney} off & free shipping with {brandCompanyName}. <LinkFormatting>View details</LinkFormatting>"
            description={{
              text: "Banner at the top of the page that promotes a Faire Direct dollar based discount with a countdown time to remain retailer to spend their Faire Direct sign up credit before expires.",
              img: "https://cdn.faire.com/fastly/aa8e003a389d5f178ac8cf7d7cf52f5cb1d4470338a6996d57ba3de644b0d027.png",
            }}
            values={{
              MediumFormatting: MediumFormatting,
              LinkFormatting: OpenFaireDirectFirstOrderIncentiveModalButton,
              FaireDirectSignUpDiscountMoney: formattedFixedBasedAmount,
              brandCompanyName: brandName ?? "",
            }}
          />
        </Typography>
      );
    }
  } else if (isLastDayBeforeExpiry) {
    if (isFaireDirectCreditPercentageBased) {
      return (
        <Typography align="center">
          <StrictLocalMsg
            defaultMessage="<MediumFormatting>Last day!</MediumFormatting> Get {faireDirectPercentageDiscountNumber} off & free shipping with {brandCompanyName}. <LinkFormatting>View details</LinkFormatting>"
            description={{
              text: "Banner at the top of the page that promotes a Faire Direct percentage based discount with a countdown time. This only show up when the remaining time is less than 24 hours (a day).",
              img: "https://cdn.faire.com/fastly/eb581d99769c338bdfd5c8ccbe22836cc51c33929ab7f22157715769d952adfc.png",
            }}
            values={{
              MediumFormatting: MediumFormatting,
              LinkFormatting: OpenFaireDirectFirstOrderIncentiveModalButton,
              faireDirectPercentageDiscountNumber:
                formattedPercentageBasedNumber,
              brandCompanyName: brandName ?? "",
            }}
          />
        </Typography>
      );
    } else {
      return (
        <Typography align="center">
          <StrictLocalMsg
            defaultMessage="<MediumFormatting>Last day!</MediumFormatting> Get {FaireDirectSignUpDiscountMoney} off & free shipping with {brandCompanyName}. <LinkFormatting>View details</LinkFormatting>"
            description={{
              text: "Banner at the top of the page that promotes a Faire Direct dollar based discount with a countdown time. This only show up when the remaining time is less than 24 hours (a day).",
              img: "https://cdn.faire.com/fastly/cec73be96163c0628337289d428a740e0801fb407b17ad4b3633c81fdf810a8e.png",
            }}
            values={{
              MediumFormatting: MediumFormatting,
              LinkFormatting: OpenFaireDirectFirstOrderIncentiveModalButton,
              FaireDirectSignUpDiscountMoney: formattedFixedBasedAmount,
              brandCompanyName: brandName ?? "",
            }}
          />
        </Typography>
      );
    }
  } else {
    // When it is not the first or last day of the auto-extend FD credit period
    if (isFaireDirectCreditPercentageBased) {
      return (
        <Typography align="center">
          <StrictLocalMsg
            defaultMessage="<MediumFormatting>{remainingDaysNumber} days left!</MediumFormatting> Get {faireDirectPercentageDiscountNumber} off & free shipping with {brandCompanyName}. <LinkFormatting>View details</LinkFormatting>"
            description={{
              text: "Banner at the top of the page that promotes a Faire Direct percentage based discount with the number of remaining days",
              img: "https://cdn.faire.com/fastly/40e748489786f14c8d14ea3231758f12421883271e2236fceeab1585eb800d09.png",
            }}
            values={{
              MediumFormatting: MediumFormatting,
              LinkFormatting: OpenFaireDirectFirstOrderIncentiveModalButton,
              remainingDaysNumber: remainingDaysBeforeExpiry,
              faireDirectPercentageDiscountNumber:
                formattedPercentageBasedNumber,
              brandCompanyName: brandName ?? "",
            }}
          />
        </Typography>
      );
    } else {
      return (
        <Typography align="center">
          <StrictLocalMsg
            defaultMessage="<MediumFormatting>{remainingDaysNumber} days left!</MediumFormatting> Get {FaireDirectSignUpDiscountMoney} off & free shipping with {brandCompanyName}. <LinkFormatting>View details</LinkFormatting>"
            description={{
              text: "Banner at the top of the page that promotes a Faire Direct dollar based discount with the number of remaining days",
              img: "https://cdn.faire.com/fastly/c59f0dc2c8fa7c5805ab489c2d828f7a165414f475d0f6016a11ae2799ab261d.png",
            }}
            values={{
              MediumFormatting: MediumFormatting,
              LinkFormatting: OpenFaireDirectFirstOrderIncentiveModalButton,
              remainingDaysNumber: remainingDaysBeforeExpiry,
              FaireDirectSignUpDiscountMoney: formattedFixedBasedAmount,
              brandCompanyName: brandName ?? "",
            }}
          />
        </Typography>
      );
    }
  }
};
